import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import StaffSidebarLayout from "./StaffSidebarLayout";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import "./staffStyles.css";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const isImportedBooking = (booking) =>
  booking?.isImported === true ||
  booking?.paidInExternalSystem === true ||
  booking?.bookingType === "manual" ||
  !!booking?.importedAt;

const resolveProfessionalTipEarnings = (booking) => {
  const bookingTip = Number(booking?.tipAmount) || 0;
  const splitProfessionalTip = Number(
    booking?.paymentSplit?.professionalTipEarnings,
  );

  if (Number.isFinite(splitProfessionalTip)) {
    if (splitProfessionalTip > 0 || bookingTip <= 0) {
      return Math.max(0, splitProfessionalTip);
    }
  }

  const splitTipAmount = Number(booking?.paymentSplit?.tipAmount);
  if (Number.isFinite(splitTipAmount)) {
    if (splitTipAmount > 0 || bookingTip <= 0) {
      return Math.max(0, splitTipAmount);
    }
  }

  return Math.max(0, bookingTip);
};

const getProfessionalBookingBreakdown = (booking, staffProfile) => {
  if (!booking) {
    return { total: 0, service: 0, tip: 0 };
  }

  if (booking.paymentSplit?.professionalEarnings !== undefined) {
    const tip = resolveProfessionalTipEarnings(booking);
    const total = Number(booking.paymentSplit.professionalEarnings) || 0;
    const service =
      booking.paymentSplit.professionalServiceEarnings !== undefined
        ? Number(booking.paymentSplit.professionalServiceEarnings) || 0
        : Math.max(0, total - tip);

    return { total, service, tip };
  }

  const grossService = Number(booking.servicePriceAtBooking) || 0;
  const rawTip = Number(booking.tipAmount) || 0;

  if (staffProfile?.commissionType === "percentage") {
    const service =
      grossService * ((100 - (staffProfile.commissionRate || 0)) / 100);
    return { total: service + rawTip, service, tip: rawTip };
  }

  if (staffProfile?.commissionType === "flat") {
    const service = Math.max(
      0,
      grossService - (staffProfile.flatCommissionAmount || 0),
    );
    return { total: service + rawTip, service, tip: rawTip };
  }

  return { total: grossService + rawTip, service: grossService, tip: rawTip };
};

const StaffEarningsPerformance = () => {
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState("overview"); // 'overview', 'payouts', 'performance'

  // Data states
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [staffProfile, setStaffProfile] = useState(null);
  const [dateRange, setDateRange] = useState("currentMonth"); // Add currentMonth and currentYear options

  // Responsive
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem("ogestyle_token");

    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const [profileRes, bookingsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/staff/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE_URL}/api/bookings/staff`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const profile = profileRes.data.staff || profileRes.data;
      setStaffProfile(profile);
      setBookings(bookingsRes.data.bookings || bookingsRes.data || []);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Filter bookings by date range (past bookings only for completed revenue)
  const getFilteredBookings = useCallback(() => {
    const now = dayjs();
    let startDate, endDate;

    if (dateRange === "currentMonth") {
      startDate = now.startOf("month");
      endDate = now.endOf("day");
    } else if (dateRange === "currentYear") {
      startDate = now.startOf("year");
      endDate = now.endOf("day");
    } else {
      const daysToShow =
        dateRange === "7days"
          ? 7
          : dateRange === "30days"
            ? 30
            : dateRange === "90days"
              ? 90
              : 365;
      startDate = now.subtract(daysToShow, "day").startOf("day");
      endDate = now.endOf("day");
    }

    return bookings.filter((b) => {
      if (isImportedBooking(b)) return false;

      const bookingDate = dayjs(b.appointmentDateTimeUTC || b.appointmentDate);
      // Only include bookings up to now (not future bookings)
      const isPastOrToday =
        bookingDate.isBefore(endDate) || bookingDate.isSame(endDate, "day");
      const isInRange =
        bookingDate.isAfter(startDate) || bookingDate.isSame(startDate, "day");

      // Log first few for debugging
      if (bookings.indexOf(b) < 3) {
      }

      return isInRange && isPastOrToday;
    });
  }, [bookings, dateRange]);

  // Get future bookings for forecasted revenue (exclude imported bookings)
  const getFutureBookings = useCallback(() => {
    const now = dayjs();
    return bookings.filter((b) => {
      // Exclude imported/manual bookings from old platform
      const isImported = isImportedBooking(b);
      if (isImported) return false;

      const bookingDate = dayjs(b.appointmentDate);
      // Include today's bookings AND future bookings that aren't completed yet
      const isFutureOrToday =
        bookingDate.isAfter(now.endOf("day")) || bookingDate.isSame(now, "day");
      const isNotFinalized = !["completed", "cancelled", "no-show"].includes(
        b.status,
      );
      return isFutureOrToday && isNotFinalized;
    });
  }, [bookings]);

  // Calculate metrics
  const calculateMetrics = useCallback(() => {
    const filtered = getFilteredBookings();
    const completed = filtered.filter((b) => b.status === "completed");
    const future = getFutureBookings();
    const completedBreakdowns = completed.map((booking) =>
      getProfessionalBookingBreakdown(booking, staffProfile),
    );

    const professionalServiceEarnings = completedBreakdowns.reduce(
      (sum, breakdown) => sum + breakdown.service,
      0,
    );
    const tipRevenue = completedBreakdowns.reduce(
      (sum, breakdown) => sum + breakdown.tip,
      0,
    );
    const totalEarnings = completedBreakdowns.reduce(
      (sum, breakdown) => sum + breakdown.total,
      0,
    );

    // Forecasted revenue from future bookings (already filtered by getFutureBookings)
    // Apply commission split to forecasted revenue too
    const forecastedRevenue = future.reduce(
      (sum, booking) =>
        sum + getProfessionalBookingBreakdown(booking, staffProfile).total,
      0,
    );

    const totalRevenue = professionalServiceEarnings;
    const avgBookingValue =
      completed.length > 0 ? totalEarnings / completed.length : 0;

    return {
      totalBookings: filtered.length,
      completedBookings: completed.length,
      cancelledBookings: filtered.filter((b) => b.status === "cancelled")
        .length,
      noShowBookings: filtered.filter((b) => b.status === "no-show").length,
      totalRevenue,
      serviceRevenue: professionalServiceEarnings, // Use professional's earnings, not gross revenue
      tipRevenue,
      forecastedRevenue,
      futureBookingsCount: future.length,
      avgBookingValue,
      completionRate:
        filtered.length > 0 ? (completed.length / filtered.length) * 100 : 0,
    };
  }, [
    getFilteredBookings,
    getFutureBookings,
    bookings,
    dateRange,
    staffProfile,
  ]);

  // Memoize metrics to prevent infinite re-renders
  const metrics = useMemo(() => calculateMetrics(), [calculateMetrics]);

  // Revenue chart data
  const getRevenueChartData = () => {
    const filtered = getFilteredBookings().filter(
      (b) => b.status === "completed",
    );
    const dataMap = {};

    const now = dayjs();

    if (dateRange === "currentMonth") {
      const startOfMonth = now.startOf("month");
      const daysInRange = now.diff(startOfMonth, "day") + 1;
      for (let i = 0; i < daysInRange; i++) {
        const date = startOfMonth.add(i, "day").format("MMM D");
        dataMap[date] = { services: 0, tips: 0 };
      }
    } else if (dateRange === "currentYear") {
      const startOfYear = now.startOf("year");
      const daysInRange = now.diff(startOfYear, "day") + 1;
      for (let i = 0; i < daysInRange; i++) {
        const date = startOfYear.add(i, "day").format("MMM D");
        dataMap[date] = { services: 0, tips: 0 };
      }
    } else {
      const daysToShow =
        dateRange === "7days"
          ? 7
          : dateRange === "30days"
            ? 30
            : dateRange === "90days"
              ? 90
              : 365;
      for (let i = daysToShow - 1; i >= 0; i--) {
        const date = now.subtract(i, "day").format("MMM D");
        dataMap[date] = { services: 0, tips: 0 };
      }
    }

    filtered.forEach((booking) => {
      const date = dayjs(booking.appointmentDate).format("MMM D");
      if (dataMap[date]) {
        const breakdown = getProfessionalBookingBreakdown(
          booking,
          staffProfile,
        );
        dataMap[date].services += breakdown.service;
        dataMap[date].tips += breakdown.tip;
      }
    });

    const dates = Object.keys(dataMap);

    return {
      labels: dates,
      datasets: [
        {
          label: "Service Revenue",
          data: dates.map((date) => dataMap[date].services),
          borderColor: "#3b82f6",
          backgroundColor: "rgba(59, 130, 246, 0.1)",
          tension: 0.4,
          fill: true,
        },
        {
          label: "Tips",
          data: dates.map((date) => dataMap[date].tips),
          borderColor: "#f59e0b",
          backgroundColor: "rgba(245, 158, 11, 0.1)",
          tension: 0.4,
          fill: true,
        },
      ],
    };
  };

  // Booking status pie chart
  const getBookingStatusData = () => {
    const filtered = getFilteredBookings();
    const statusCount = {
      completed: 0,
      confirmed: 0,
      cancelled: 0,
      "no-show": 0,
      pending: 0,
    };

    filtered.forEach((booking) => {
      const status = booking.status || "pending";
      if (statusCount.hasOwnProperty(status)) {
        statusCount[status]++;
      }
    });

    return {
      labels: ["Completed", "Confirmed", "Cancelled", "No-Show", "Pending"],
      datasets: [
        {
          data: [
            statusCount.completed,
            statusCount.confirmed,
            statusCount.cancelled,
            statusCount["no-show"],
            statusCount.pending,
          ],
          backgroundColor: [
            "#10b981",
            "#3b82f6",
            "#ef4444",
            "#f59e0b",
            "#6b7280",
          ],
        },
      ],
    };
  };

  // Top services chart
  const getTopServicesData = () => {
    const filtered = getFilteredBookings().filter(
      (b) => b.status === "completed",
    );
    const serviceMap = {};

    filtered.forEach((booking) => {
      const serviceName = booking.serviceId?.name || "Unknown Service";
      if (!serviceMap[serviceName]) {
        serviceMap[serviceName] = { count: 0, revenue: 0 };
      }
      const breakdown = getProfessionalBookingBreakdown(booking, staffProfile);
      serviceMap[serviceName].count += 1;
      serviceMap[serviceName].revenue += breakdown.service;
    });

    const sorted = Object.entries(serviceMap)
      .sort((a, b) => b[1].revenue - a[1].revenue)
      .slice(0, 5);

    return {
      labels: sorted.map(([name]) => name),
      datasets: [
        {
          label: "Revenue ($)",
          data: sorted.map(([, data]) => data.revenue),
          backgroundColor: "#3b82f6",
        },
      ],
    };
  };

  // Busiest days chart
  const getBusiestDaysData = () => {
    const filtered = getFilteredBookings();
    const dayCount = {
      Sunday: 0,
      Monday: 0,
      Tuesday: 0,
      Wednesday: 0,
      Thursday: 0,
      Friday: 0,
      Saturday: 0,
    };

    filtered.forEach((booking) => {
      const dayName = dayjs(booking.appointmentDate).format("dddd");
      if (dayCount.hasOwnProperty(dayName)) {
        dayCount[dayName]++;
      }
    });

    return {
      labels: Object.keys(dayCount),
      datasets: [
        {
          label: "Bookings",
          data: Object.values(dayCount),
          backgroundColor: [
            "#ef4444",
            "#f59e0b",
            "#10b981",
            "#06b6d4",
            "#3b82f6",
            "#8b5cf6",
            "#ec4899",
          ],
        },
      ],
    };
  };

  if (loading) {
    return (
      <StaffSidebarLayout>
        <div style={{ textAlign: "center", padding: "60px 20px" }}>
          <div style={{ fontSize: "18px", color: "#6b7280" }}>Loading...</div>
        </div>
      </StaffSidebarLayout>
    );
  }

  return (
    <StaffSidebarLayout>
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "20px" }}>
        {/* Header */}
        <div style={{ marginBottom: "24px" }}>
          <h1
            style={{
              fontSize: "28px",
              fontWeight: "700",
              color: "#1f2937",
              marginBottom: "8px",
            }}
          >
            💰 My Earnings & Performance
          </h1>
          <p style={{ color: "#6b7280", fontSize: "14px" }}>
            Track your earnings, performance, and payouts
          </p>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            gap: "8px",
            borderBottom: "2px solid #e5e7eb",
            marginBottom: "24px",
            overflowX: "auto",
          }}
        >
          {[
            { id: "overview", label: "📊 Overview" },
            { id: "performance", label: "📈 Performance" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: "12px 24px",
                border: "none",
                background: activeTab === tab.id ? "#3b82f6" : "transparent",
                color: activeTab === tab.id ? "white" : "#6b7280",
                fontWeight: activeTab === tab.id ? "600" : "500",
                fontSize: "14px",
                borderRadius: "8px 8px 0 0",
                cursor: "pointer",
                transition: "all 0.2s",
                whiteSpace: "nowrap",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <div>
            {/* Date Range Filter */}
            <div
              style={{
                marginBottom: "24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {[
                  "currentMonth",
                  "currentYear",
                  "7days",
                  "30days",
                  "90days",
                  "365days",
                ].map((range) => (
                  <button
                    key={range}
                    onClick={() => setDateRange(range)}
                    style={{
                      padding: "8px 16px",
                      border:
                        dateRange === range
                          ? "2px solid #3b82f6"
                          : "2px solid #e5e7eb",
                      background: dateRange === range ? "#eff6ff" : "white",
                      color: dateRange === range ? "#3b82f6" : "#6b7280",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: "500",
                    }}
                  >
                    {range === "currentMonth"
                      ? "This Month"
                      : range === "currentYear"
                        ? "This Year"
                        : range === "7days"
                          ? "Last 7 Days"
                          : range === "30days"
                            ? "Last 30 Days"
                            : range === "90days"
                              ? "Last 90 Days"
                              : "Last Year"}
                  </button>
                ))}
              </div>
            </div>

            {/* Key Metrics Cards */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : "repeat(auto-fit, minmax(250px, 1fr))",
                gap: "16px",
                marginBottom: "24px",
              }}
            >
              <div
                style={{
                  background:
                    "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                  padding: "20px",
                  borderRadius: "12px",
                  color: "white",
                  boxShadow: "0 4px 6px rgba(59, 130, 246, 0.3)",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    opacity: 0.9,
                    marginBottom: "8px",
                  }}
                >
                  TOTAL REVENUE
                </div>
                <div style={{ fontSize: "32px", fontWeight: "700" }}>
                  ${metrics.totalRevenue.toFixed(2)}
                </div>
                <div style={{ fontSize: "11px", opacity: 0.8, marginTop: "4px" }}>
                  {dateRange === "currentMonth"
                    ? "This calendar month"
                    : dateRange === "currentYear"
                      ? "This calendar year"
                      : `Last ${
                          dateRange === "7days"
                            ? "7"
                            : dateRange === "30days"
                              ? "30"
                              : dateRange === "90days"
                                ? "90"
                                : "365"
                        } days`}
                </div>
              </div>

              <div
                style={{
                  background: "white",
                  padding: "20px",
                  borderRadius: "12px",
                  border: "1px solid #e5e7eb",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    marginBottom: "8px",
                  }}
                >
                  COMPLETED BOOKINGS
                </div>
                <div
                  style={{
                    fontSize: "32px",
                    fontWeight: "700",
                    color: "#1f2937",
                  }}
                >
                  {metrics.completedBookings}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "#10b981",
                    marginTop: "4px",
                  }}
                >
                  {metrics.completionRate.toFixed(0)}% completion rate
                </div>
              </div>

              <div
                style={{
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  padding: "20px",
                  borderRadius: "12px",
                  color: "white",
                  boxShadow: "0 4px 6px rgba(245, 158, 11, 0.3)",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    opacity: 0.9,
                    marginBottom: "8px",
                  }}
                >
                  TIPS RECEIVED
                </div>
                <div style={{ fontSize: "32px", fontWeight: "700" }}>
                  ${metrics.tipRevenue.toFixed(2)}
                </div>
                <div
                  style={{ fontSize: "11px", opacity: 0.8, marginTop: "4px" }}
                >
                  $
                  {(metrics.completedBookings > 0
                    ? metrics.tipRevenue / metrics.completedBookings
                    : 0
                  ).toFixed(2)}{" "}
                  avg
                </div>
              </div>

              <div
                style={{
                  background: "white",
                  padding: "20px",
                  borderRadius: "12px",
                  border: "1px solid #e5e7eb",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    marginBottom: "8px",
                  }}
                >
                  AVG BOOKING VALUE
                </div>
                <div
                  style={{
                    fontSize: "32px",
                    fontWeight: "700",
                    color: "#1f2937",
                  }}
                >
                  ${metrics.avgBookingValue.toFixed(2)}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "#6b7280",
                    marginTop: "4px",
                  }}
                >
                  Per booking
                </div>
              </div>

              <div
                style={{
                  background:
                    "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  padding: "20px",
                  borderRadius: "12px",
                  color: "white",
                  boxShadow: "0 4px 6px rgba(16, 185, 129, 0.3)",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    opacity: 0.9,
                    marginBottom: "8px",
                  }}
                >
                  FORECASTED REVENUE
                </div>
                <div style={{ fontSize: "32px", fontWeight: "700" }}>
                  ${metrics.forecastedRevenue.toFixed(2)}
                </div>
                <div
                  style={{ fontSize: "11px", opacity: 0.8, marginTop: "4px" }}
                >
                  {metrics.futureBookingsCount} upcoming booking
                  {metrics.futureBookingsCount !== 1 ? "s" : ""}
                </div>
              </div>
            </div>

            {/* Revenue Breakdown */}
            <div
              style={{
                background: "white",
                padding: "20px",
                borderRadius: "12px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                marginBottom: "24px",
              }}
            >
              <h2
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  marginBottom: "16px",
                }}
              >
                💰 Revenue Breakdown
              </h2>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: "12px",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      marginBottom: "4px",
                    }}
                  >
                    Service Revenue
                  </div>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: "600",
                      color: "#3b82f6",
                    }}
                  >
                    ${metrics.serviceRevenue.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      marginBottom: "4px",
                    }}
                  >
                    Tips
                  </div>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: "600",
                      color: "#f59e0b",
                    }}
                  >
                    ${metrics.tipRevenue.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>

            {/* Revenue Trend Chart */}
            <div
              style={{
                background: "white",
                padding: "20px",
                borderRadius: "12px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                height: "350px",
              }}
            >
              <h2
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  marginBottom: "16px",
                }}
              >
                📈 Revenue Trend
              </h2>
              <div style={{ height: "250px" }}>
                <Line
                  data={getRevenueChartData()}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: "bottom" },
                      tooltip: {
                        callbacks: {
                          label: (context) =>
                            `${context.dataset.label}: $${context.parsed.y.toFixed(2)}`,
                        },
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          callback: (value) => "$" + value,
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === "performance" && (
          <div>
            {/* Date Range Filter */}
            <div
              style={{
                marginBottom: "24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {["7days", "30days", "90days", "365days"].map((range) => (
                  <button
                    key={range}
                    onClick={() => setDateRange(range)}
                    style={{
                      padding: "8px 16px",
                      border:
                        dateRange === range
                          ? "2px solid #3b82f6"
                          : "2px solid #e5e7eb",
                      background: dateRange === range ? "#eff6ff" : "white",
                      color: dateRange === range ? "#3b82f6" : "#6b7280",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: "500",
                    }}
                  >
                    {range === "7days"
                      ? "Last 7 Days"
                      : range === "30days"
                        ? "Last 30 Days"
                        : range === "90days"
                          ? "Last 90 Days"
                          : "Last Year"}
                  </button>
                ))}
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                gap: "24px",
              }}
            >
              {/* Booking Status */}
              <div
                style={{
                  background: "white",
                  padding: "20px",
                  borderRadius: "12px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  height: "350px",
                }}
              >
                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    marginBottom: "16px",
                  }}
                >
                  📊 Booking Status
                </h3>
                <div style={{ height: "250px" }}>
                  <Doughnut
                    data={getBookingStatusData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { position: "bottom" },
                      },
                    }}
                  />
                </div>
              </div>

              {/* Top Services */}
              <div
                style={{
                  background: "white",
                  padding: "20px",
                  borderRadius: "12px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  height: "350px",
                }}
              >
                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    marginBottom: "16px",
                  }}
                >
                  ⭐ Top Services
                </h3>
                <div style={{ height: "250px" }}>
                  <Bar
                    data={getTopServicesData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          callbacks: {
                            label: (context) =>
                              `$${context.parsed.y.toFixed(2)}`,
                          },
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback: (value) => "$" + value,
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>

              {/* Busiest Days - Full Width */}
              <div
                style={{
                  background: "white",
                  padding: "20px",
                  borderRadius: "12px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  gridColumn: isMobile ? "1" : "span 2",
                  height: "350px",
                }}
              >
                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    marginBottom: "16px",
                  }}
                >
                  📅 Busiest Days
                </h3>
                <div style={{ height: "250px" }}>
                  <Bar
                    data={getBusiestDaysData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            stepSize: 1,
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </StaffSidebarLayout>
  );
};

export default StaffEarningsPerformance;
